import { DOCUMENT } from '@angular/common';
import { Injectable, InjectionToken, inject } from '@angular/core';

/**
 * Llave de la API de Google Maps (JavaScript). Sin llave no hay mapa: los componentes muestran un aviso y
 * dejan capturar las coordenadas a mano. La llave NO va en el repositorio; cada entorno la aporta con este
 * token. Se provee en `main.ts` desde `environment.googleMaps.apiKey`.
 */
export const GOOGLE_MAPS_API_KEY = new InjectionToken<string | null>('GOOGLE_MAPS_API_KEY', {
  providedIn: 'root',
  factory: () => null,
});

/** Map ID del mapa (obligatorio para el marcador avanzado). Sin él no hay mapa: se trata como no disponible. */
export const GOOGLE_MAPS_MAP_ID = new InjectionToken<string | null>('GOOGLE_MAPS_MAP_ID', {
  providedIn: 'root',
  factory: () => null,
});

const MAPS_READY_CALLBACK = '__auroraGoogleMapsReady';

/** Lo que usan los componentes de la API de Google Maps. */
export interface GoogleMapsApi {
  Map: typeof google.maps.Map;
  AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement;
}

/** Lo que usa el mapa para abrir Street View mirando hacia el pin. */
export interface StreetViewApi {
  service: google.maps.StreetViewService;
  /** Solo panoramas de calle: los de interiores no sirven para ubicar una fachada. */
  outdoor: google.maps.StreetViewSource;
  computeHeading: typeof google.maps.geometry.spherical.computeHeading;
}

/**
 * Carga la API de Google Maps una sola vez, bajo demanda. Devuelve `null` si no hay llave o si el script no
 * se pudo cargar, para que quien lo use degrade sin romperse.
 */
@Injectable({ providedIn: 'root' })
export class GoogleMapsLoaderService {
  private readonly key = inject(GOOGLE_MAPS_API_KEY);
  private readonly mapId = inject(GOOGLE_MAPS_MAP_ID);
  private readonly document = inject(DOCUMENT);
  private pending: Promise<GoogleMapsApi | null> | null = null;

  /** Hay llave y Map ID configurados (no garantiza que sean válidos: Google los valida al cargar el mapa). */
  get available(): boolean {
    return !!this.key && !!this.mapId;
  }

  load(): Promise<GoogleMapsApi | null> {
    if (!this.key || !this.mapId) return Promise.resolve(null);
    this.pending ??= this.injectScript(this.key)
      .then(async () => {
        const { Map } = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary;
        const { AdvancedMarkerElement } = (await google.maps.importLibrary(
          'marker',
        )) as google.maps.MarkerLibrary;
        return { Map, AdvancedMarkerElement };
      })
      .catch(() => {
        // Permite reintentar si falló la red; los componentes siguen funcionando sin mapa
        this.pending = null;
        return null;
      });
    return this.pending;
  }

  /** Geocoding solo se pide al ubicar una dirección, no al abrir el mapa. `null` si no hay llave o falla. */
  async geocoder(): Promise<google.maps.Geocoder | null> {
    if (!(await this.load())) return null;
    try {
      const { Geocoder } = (await google.maps.importLibrary(
        'geocoding',
      )) as google.maps.GeocodingLibrary;
      return new Geocoder();
    } catch {
      return null;
    }
  }

  /** Places (autocompletado) solo se pide al buscar una dirección. `null` si no hay llave o falla. */
  async places(): Promise<google.maps.PlacesLibrary | null> {
    if (!(await this.load())) return null;
    try {
      return (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary;
    } catch {
      return null;
    }
  }

  /** Street View solo se pide al querer ver la calle: cada panorama cuenta en Google. `null` si no hay llave o falla. */
  async streetView(): Promise<StreetViewApi | null> {
    if (!(await this.load())) return null;
    try {
      const [{ StreetViewService, StreetViewSource }, { spherical }] = await Promise.all([
        google.maps.importLibrary('streetView') as Promise<google.maps.StreetViewLibrary>,
        google.maps.importLibrary('geometry') as Promise<google.maps.GeometryLibrary>,
      ]);
      return {
        service: new StreetViewService(),
        outdoor: StreetViewSource.OUTDOOR,
        computeHeading: spherical.computeHeading,
      };
    } catch {
      return null;
    }
  }

  private injectScript(key: string): Promise<void> {
    if (typeof google !== 'undefined' && typeof google.maps?.importLibrary === 'function')
      return Promise.resolve();
    return new Promise((resolve, reject) => {
      // Con loading=async, `importLibrary` todavía no existe en el onload del script: Google avisa por el callback
      const win = this.document.defaultView as unknown as Record<string, unknown>;
      win[MAPS_READY_CALLBACK] = () => {
        delete win[MAPS_READY_CALLBACK];
        resolve();
      };
      const script = this.document.createElement('script');
      script.async = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&v=weekly&loading=async&language=es&region=MX&callback=${MAPS_READY_CALLBACK}`;
      script.onerror = () => {
        // Se quita para que un reintento no deje dos scripts de Google en la página
        script.remove();
        delete win[MAPS_READY_CALLBACK];
        reject(new Error('No se pudo cargar Google Maps'));
      };
      this.document.head.appendChild(script);
    });
  }
}
